// Type-only namespace import — erased at compile time, so the runtime module
// still loads exclusively via the dynamic import() below (own chunk).
import type * as SignalRTypes from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';

import { SIGNALR_VESR } from '../constants/global';
import type { IIncident } from '../interface/vesr';
import { resolveVesrBearer } from './vesr-auth';

// Live incident updates from the VESR hub. Trimmed from the admin app's
// VesrSignalRService: the agent client only joins its state room (incident
// events broadcast to `state_{stateCode}` — agent-created incidents carry no
// electionId, so election rooms never fire for them) and only listens for
// IncidentCreated/IncidentUpdated. `@microsoft/signalr` is imported
// dynamically so it chunks separately from the entry bundle; the connection
// lives only while the Reports hub panel is open. Failures are contained —
// they never affect the agent's session.

export type VesrConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type IncidentEvent = 'IncidentCreated' | 'IncidentUpdated';

type IncidentHandler = (incident: IIncident) => void;

type SignalRModule = typeof SignalRTypes;

const RETRY_DELAYS_MS = [0, 2000, 5000, 10_000, 30_000];

export class VesrIncidentLiveClient {
	private signalR: SignalRModule | null = null;
	private connection: HubConnection | null = null;
	private statusCallback: ((status: VesrConnectionStatus) => void) | null = null;
	private joinedStates = new Set<string>();
	private handlers = new Map<IncidentEvent, IncidentHandler>();
	private disposed = false;

	async connect(onStatusChange?: (status: VesrConnectionStatus) => void): Promise<void> {
		this.disposed = false;
		this.statusCallback = onStatusChange ?? null;
		this.statusCallback?.('connecting');

		try {
			this.signalR = this.signalR ?? (await import('@microsoft/signalr'));
			this.buildConnection();
			await this.connection!.start();
			if (this.disposed) return;
			this.statusCallback?.('connected');
			await this.rejoinRooms();
		} catch (error) {
			console.warn('VESR live connection failed:', error);
			this.statusCallback?.('disconnected');
		}
	}

	async disconnect(): Promise<void> {
		this.disposed = true;
		this.joinedStates.clear();
		if (this.connection) {
			const connection = this.connection;
			this.connection = null;
			try {
				await connection.stop();
			} catch {
				// Already closing — nothing to do.
			}
		}
	}

	async joinStateRoom(stateCode: string): Promise<void> {
		this.joinedStates.add(stateCode);
		await this.invokeIfConnected('JoinStateRoom', stateCode);
	}

	on(event: IncidentEvent, handler: IncidentHandler): void {
		this.handlers.set(event, handler);
		this.connection?.on(event, handler);
	}

	off(event: IncidentEvent): void {
		this.handlers.delete(event);
		this.connection?.off(event);
	}

	private buildConnection(): void {
		const signalR = this.signalR!;
		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(SIGNALR_VESR, {
				// VESR-issued token where available (staging exchange), else the
				// core JWT (production normalizes its claims). See vesr-auth.ts.
				accessTokenFactory: () => resolveVesrBearer(),
			})
			.configureLogging(signalR.LogLevel.Warning)
			.withAutomaticReconnect(RETRY_DELAYS_MS)
			.build();

		this.connection.onreconnecting(() => this.statusCallback?.('reconnecting'));
		this.connection.onreconnected(() => {
			this.statusCallback?.('connected');
			void this.rejoinRooms();
		});
		this.connection.onclose(() => this.statusCallback?.('disconnected'));

		for (const [event, handler] of this.handlers) {
			this.connection.on(event, handler);
		}
	}

	private async invokeIfConnected(method: string, ...args: unknown[]): Promise<void> {
		if (!this.signalR || !this.connection) return;
		if (this.connection.state !== this.signalR.HubConnectionState.Connected) return;
		try {
			await this.connection.invoke(method, ...args);
		} catch (error) {
			console.warn(`VESR live invoke ${method} failed:`, error);
		}
	}

	private async rejoinRooms(): Promise<void> {
		for (const stateCode of this.joinedStates) {
			await this.invokeIfConnected('JoinStateRoom', stateCode);
		}
	}
}
